/*
 * =============================================================================
 * FILE HEADER — apps/studio/src/lib/assist/resolver.ts
 * =============================================================================
 *
 * CHANGE LOG
 * ----------
 * | # | Date & Time (IST)   | Who               | Why                       | What Changed          |
 * |---|---------------------|-------------------|---------------------------|------------------------|
 * | 1 | 2026-07-06          | Claude Sonnet 5   | AI Assist port (from Plex)| Created — Lane 1       |
 * |   |                     |                   |                           | element resolution     |
 * |   |                     |                   |                           | ladder against the     |
 * |   |                     |                   |                           | live A11yNode tree     |
 *
 * WHICH FILE CALLS / IMPORTS THIS FILE
 * --------------------------------------
 * - apps/studio/src/lib/assist/buildStep.ts
 * - apps/studio/src/components/AssistBar.tsx
 * - apps/studio/src/lib/assist/__tests__/resolver.test.ts
 *
 * FUNCTIONS / EXPORTS COVERED
 * ----------------------------
 * - resolveTarget  — phrase (+ optional role filter) -> ResolveResult, against A11yNode[]
 * - ResolvedMatch / ResolveResult / ResolveOptions — types
 *
 * IMPORTANT POINTS
 * -----------------
 * 1. Ported from D:\Plex\apps\studio-web\src\lib\assist\resolver.ts. Zero tokens.
 *    Ladder: exact accessible-name match -> normalized/contains match -> ONNX
 *    embedding re-rank (all-MiniLM-L6-v2, via @mimiq/healing's defaultEmbed —
 *    the SAME lazy-singleton model used by Tier-1 replay healing, so no
 *    duplicate model load).
 * 2. Operates on Mimiq's A11yNode[] tree (apps/studio/src/device/treeParser.ts)
 *    instead of Plex's browser-side SerializedAXTree. Reuses `flattenTree` and
 *    `isInteractive` from recorder.ts rather than reimplementing tree walking —
 *    those are the exact same helpers Drive-mode tap recording already uses.
 * 3. accessibleName(node) = contentDesc || text, matching buildSelector's own
 *    `pickLabel` priority in recorder.ts (contentDesc first) so Assist and
 *    Drive-mode agree on what "the name of an element" means.
 * 4. Role filter uses Android `className` SUBSTRING matching (grammar.ts maps
 *    spoken nouns to className fragments like "Button"/"EditText") — a HINT,
 *    not a hard constraint: if the filtered pool yields no exact/contains
 *    match, stages 1-2 retry against the full unfiltered pool before falling
 *    through to embeddings.
 * 5. Never auto-picks under ambiguity: `ambiguous:true` means the caller MUST
 *    show top candidates for a human click, never silently choose one.
 * 6. Accept thresholds (embedding stage): score >= 0.55, margin >= 0.08 between
 *    top-1 and top-2 — identical to Plex's authoring-time thresholds (looser
 *    than replay-time Tier-1 healing's 0.60/0.07 since a human is in the loop
 *    here to reject a bad match).
 * =============================================================================
 */

import type { A11yNode } from "../../device/treeParser.js";
import { flattenTree, isInteractive } from "../../device/recorder.js";
import { defaultEmbed } from "@mimiq/healing";

export interface ResolvedMatch {
  node: A11yNode;
  accessibleName: string;
  strategy: "exact" | "contains" | "embedding";
  score: number;
}

export interface ResolveResult {
  matches: ResolvedMatch[];
  ambiguous: boolean;
  /**
   * Set ONLY when matches is empty, to distinguish WHY nothing was found —
   * without this, "the local ONNX model failed to load" and "genuinely no
   * such element on screen" both look like an identical bare "no match" to
   * the user, which makes real failures undebuggable.
   */
  reason?: "model-unavailable" | "no-match";
}

export interface ResolveOptions {
  /** Search all text-bearing nodes, not just interactive ones (used for "page contains" assertions). */
  includeNonInteractive?: boolean;
  minEmbeddingScore?: number;
  minEmbeddingMargin?: number;
}

export function accessibleName(node: A11yNode): string {
  return (node.contentDesc || node.text || "").trim();
}

function normalize(s: string): string {
  return s.toLowerCase().trim().replace(/\s+/g, " ");
}

function roleMatches(node: A11yNode, filter: string): boolean {
  return node.className.includes(filter);
}

function buildNodeSemanticText(node: A11yNode): string {
  const parts: string[] = [];
  if (node.className) parts.push(node.className);
  if (node.text) parts.push(node.text);
  if (node.contentDesc && node.contentDesc !== node.text) parts.push(node.contentDesc);
  if (node.resourceId) parts.push(node.resourceId);
  return parts.join(" ").trim() || "unknown";
}

function cosineSimilarity(a: Float32Array, b: Float32Array): number {
  let dot = 0, normA = 0, normB = 0;
  for (let i = 0; i < a.length; i++) {
    dot += a[i]! * b[i]!;
    normA += a[i]! * a[i]!;
    normB += b[i]! * b[i]!;
  }
  const denom = Math.sqrt(normA) * Math.sqrt(normB);
  return denom === 0 ? 0 : Math.max(0, Math.min(1, dot / denom));
}

function toMatch(node: A11yNode, strategy: ResolvedMatch["strategy"], score: number): ResolvedMatch {
  return { node, accessibleName: accessibleName(node), strategy, score };
}

/** Stage 1 (exact) + Stage 2 (contains) against a given pool. Null = no match in this pool. */
function matchStringStages(pool: A11yNode[], normPhrase: string): ResolveResult | null {
  const exact = pool.filter((n) => normalize(accessibleName(n)) === normPhrase);
  if (exact.length === 1) return { matches: [toMatch(exact[0]!, "exact", 1)], ambiguous: false };
  if (exact.length > 1) return { matches: exact.slice(0, 3).map((n) => toMatch(n, "exact", 1)), ambiguous: true };

  const contains = pool.filter((n) => {
    const name = normalize(accessibleName(n));
    return name.length > 0 && (name.includes(normPhrase) || normPhrase.includes(name));
  });
  if (contains.length === 1) return { matches: [toMatch(contains[0]!, "contains", 0.9)], ambiguous: false };
  if (contains.length > 1) {
    // Rank by length-closeness to the phrase — shorter delta = more likely intended match.
    const ranked = contains
      .map((n) => ({ n, delta: Math.abs(normalize(accessibleName(n)).length - normPhrase.length) }))
      .sort((a, b) => a.delta - b.delta);
    const top = ranked.slice(0, 3).map((r) => toMatch(r.n, "contains", 0.9));
    // Ambiguous unless the closest match is a clear standout (next-closest is much longer delta away)
    const ambiguous = ranked.length > 1 && (ranked[1]!.delta - ranked[0]!.delta) < 3;
    return { matches: top, ambiguous };
  }

  return null; // no match in this pool
}

/**
 * Resolves a natural-language target phrase against the live accessibility tree.
 * Ladder: exact accessible-name match -> contains match -> ONNX embedding re-rank.
 * Returns `ambiguous:true` when multiple candidates tie — the caller must ask
 * the human to pick, never auto-select.
 *
 * Role filter is a HINT, not a hard constraint: if the filtered pool yields no
 * exact/contains match at all, stages 1-2 retry against the full unfiltered
 * pool before falling through to embeddings — real users sometimes get the
 * role noun wrong ("the link 'Sign in'" when it's actually a Button), and
 * finding the right element beats rigidly enforcing the stated role.
 */
export async function resolveTarget(
  tree: A11yNode[],
  phrase: string,
  roleFilter?: string,
  opts: ResolveOptions = {},
): Promise<ResolveResult> {
  const { includeNonInteractive = false, minEmbeddingScore = 0.55, minEmbeddingMargin = 0.08 } = opts;

  const flat = flattenTree(tree);
  const all = includeNonInteractive ? flat : flat.filter(isInteractive);
  const filteredPool = roleFilter ? all.filter((n) => roleMatches(n, roleFilter)) : all;

  if (all.length === 0) return { matches: [], ambiguous: false };

  const normPhrase = normalize(phrase);
  if (!normPhrase) return { matches: [], ambiguous: false };

  // Stages 1-2 against the role-filtered pool first, then widen to `all` on a total miss.
  let searchSpace = filteredPool.length > 0 ? filteredPool : all;
  const stringResult = matchStringStages(searchSpace, normPhrase);
  if (stringResult) return stringResult;

  if (searchSpace !== all) {
    const widened = matchStringStages(all, normPhrase);
    if (widened) return widened;
    searchSpace = all; // use the full pool for the embedding stage too
  }

  // ── Stage 3: ONNX embedding re-rank (zero tokens, local, <400ms) ───────────
  let queryVec: Float32Array;
  try {
    queryVec = await defaultEmbed(phrase);
  } catch (err) {
    // Model failed to load — @huggingface/transformers fetches the ~23MB
    // all-MiniLM-L6-v2 weights on first use and caches them locally. If that
    // first download is blocked (offline, restrictive network) or errors for
    // any other reason, this is NOT the same failure as "no such element
    // exists" — surface it distinctly so it's debuggable.
    console.error("[AI Assist] ONNX embedder failed to load — falling back without embedding re-rank:", err);
    return { matches: [], ambiguous: false, reason: "model-unavailable" };
  }

  const scored = await Promise.all(
    searchSpace.map(async (n) => {
      const vec = await defaultEmbed(buildNodeSemanticText(n));
      return { node: n, score: cosineSimilarity(queryVec, vec) };
    }),
  );
  scored.sort((a, b) => b.score - a.score);
  const top = scored.slice(0, 3);

  if (top.length === 0 || top[0]!.score < minEmbeddingScore) return { matches: [], ambiguous: false, reason: "no-match" };

  const margin = top.length > 1 ? top[0]!.score - top[1]!.score : 1;
  const ambiguous = top.length > 1 && margin < minEmbeddingMargin;

  return {
    matches: top.map((t) => toMatch(t.node, "embedding", t.score)),
    ambiguous,
  };
}
